import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Switch,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useBlocker, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { CameraImageViewer } from '../../../components/CameraImageViewer';
import { QueryParams } from '../../../components/DataTable';
import { ModFilters } from '../../../components/ModFilters';
import { useAuthContext } from '../../../context/AuthContext';
import { useNotificationContext } from '../../../context/NotificationContext';
import { useImagePreloader } from '../../../hooks/useImagePreloader';
import { useStoreTimeZones } from '../../../hooks/useStoreTimeZones';
import { ApiService } from '../../../services/api';
import {
  BBox,
  CameraDetails,
  CameraImageResponse,
  CameraPog,
  CandidatesSearchParams,
  ClassificationType,
  DesignResponse,
  PogBBox,
  ProductRecognitionCropScanResponse,
  ProductVariantResponse,
  Rect,
} from '../../../types';
import { areBoxOverlapping } from '../../../utils/geometry';
import { CandidateDetailHeader } from '../components/CandidateDetailHeader';
import { CropScansList } from '../components/CropScansList';
import { UnsavedChangesDialog } from '../components/UnsavedChangesDialog';
import { useProductRecognitionScanning } from '../utils/useProductRecognitionScanning';
import { EmbeddingTypeFilter } from './EmbeddingTypeFilter';
import { PageLoadingSpinner } from './PageLoadingSpinner';
import { VariantSelectionModal } from './VariantSelectionModal';

const COLORS = {
  SELECTED_BOX: '#2196F3',
  UNSELECTED_BOX: '#FF0000',
  UPDATED_BOX: '#4CAF50',
} as const;

// Overlap detection threshold (40% minimum overlap required)
const OVERLAP_THRESHOLD = 0.4;

// For now allow for everone
const getAutoAssignSingleDesign = (_chainId: number): boolean => true;

type NewDesignIntent = {
  intentId: string;
  // groupKey identifies which crops can share this intent — typically the
  // pendingSave's itemNumber, falling back to upc when itemNumber is missing.
  groupKey: string;
  // The crop scan that originated this intent. Re-clicking "+ New design" on
  // the same scan replaces (not duplicates) its previous intent.
  originScanId: number;
  // Bbox of the originating crop on the camera image; used for the synthetic
  // tile preview (rendered via ProductRecognitionViewer, no fetch/upload).
  originBbox: [number, number, number, number];
  variantUpc: string;
  variantName: string | null;
};

const getIntentGroupKey = (pogData: { itemNumber?: string; upc: string }): string =>
  pogData.itemNumber || pogData.upc;

type PendingSave = {
  upc: string;
  ilItemId: number;
  itemNumber: string;
  productName: string | null;
  productId: number;
  shelfColumnLabel?: string; // Format: "mod-shelf-column" e.g., "1-2-3"
  orientation: 'front' | 'not-front';
  // Real design id OR a new-design intent id (`new:...`). The literal sentinel
  // 'new' is never stored here — see handleSelectDesign and handleVariantSelected.
  selectedDesignId?: string;
};

// Selection state types
interface SelectionState {
  selectedBoxes: Set<string>;
  highlightedPogBox: PogBBox | null;
  filterType: ClassificationType | 'all';
  pendingSaves: Map<number, PendingSave>;
}

type SelectionAction =
  | { type: 'SET_SELECTED_BOXES'; payload: Set<string> }
  | { type: 'CLEAR_SELECTED_BOXES' }
  | { type: 'TOGGLE_SELECTED_BOX'; payload: string }
  | { type: 'SET_HIGHLIGHTED_POG_BOX'; payload: PogBBox | null }
  | { type: 'SET_FILTER_TYPE'; payload: ClassificationType | 'all' }
  | { type: 'REMOVE_PENDING_SAVE'; payload: number }
  | {
      type: 'ADD_PENDING_SAVES';
      payload: {
        cropScans: ProductRecognitionCropScanResponse[];
        pogBox: Omit<PendingSave, 'orientation'>;
      };
    }
  | {
      type: 'SET_PENDING_ORIENTATION';
      payload: { productCropScanId: number; orientation: 'front' | 'not-front' };
    }
  | {
      type: 'SET_PENDING_SELECTED_DESIGN';
      payload: {
        productCropScanId: number;
        selectedDesignId: string | undefined;
      };
    }
  | { type: 'CLEAR_PENDING_SAVES' };

// Selection reducer
function selectionReducer(state: SelectionState, action: SelectionAction): SelectionState {
  switch (action.type) {
    case 'SET_SELECTED_BOXES':
      return { ...state, selectedBoxes: action.payload };
    case 'CLEAR_SELECTED_BOXES':
      return { ...state, selectedBoxes: new Set() };
    case 'TOGGLE_SELECTED_BOX': {
      const newSelected = new Set(state.selectedBoxes);
      if (newSelected.has(action.payload)) {
        newSelected.delete(action.payload);
      } else {
        newSelected.add(action.payload);
      }
      return { ...state, selectedBoxes: newSelected };
    }
    case 'SET_HIGHLIGHTED_POG_BOX':
      return { ...state, highlightedPogBox: action.payload };
    case 'SET_FILTER_TYPE':
      return { ...state, filterType: action.payload };
    case 'ADD_PENDING_SAVES': {
      const newPendingSaves = new Map(state.pendingSaves);
      action.payload.cropScans.forEach((cropScan) => {
        const existing = newPendingSaves.get(cropScan.product_crop_scan_id);
        newPendingSaves.set(cropScan.product_crop_scan_id, {
          upc: action.payload.pogBox.upc,
          ilItemId: action.payload.pogBox.ilItemId,
          itemNumber: action.payload.pogBox.itemNumber,
          productName: action.payload.pogBox.productName,
          productId: action.payload.pogBox.productId,
          shelfColumnLabel: action.payload.pogBox.shelfColumnLabel,
          orientation: existing?.orientation ?? 'front',
        });
      });
      return { ...state, pendingSaves: newPendingSaves };
    }
    case 'SET_PENDING_ORIENTATION': {
      const existing = state.pendingSaves.get(action.payload.productCropScanId);
      if (!existing) return state;
      const newPendingSaves = new Map(state.pendingSaves);
      newPendingSaves.set(action.payload.productCropScanId, {
        ...existing,
        orientation: action.payload.orientation,
      });
      return { ...state, pendingSaves: newPendingSaves };
    }
    case 'SET_PENDING_SELECTED_DESIGN': {
      const existing = state.pendingSaves.get(action.payload.productCropScanId);
      if (!existing) return state;
      const newPendingSaves = new Map(state.pendingSaves);
      newPendingSaves.set(action.payload.productCropScanId, {
        ...existing,
        selectedDesignId: action.payload.selectedDesignId,
      });
      return { ...state, pendingSaves: newPendingSaves };
    }
    case 'CLEAR_PENDING_SAVES':
      return { ...state, pendingSaves: new Map() };
    case 'REMOVE_PENDING_SAVE': {
      const newPendingSaves = new Map(state.pendingSaves);
      newPendingSaves.delete(action.payload);
      return { ...state, pendingSaves: newPendingSaves };
    }
    default:
      return state;
  }
}

interface CandidateDetailProps {
  cameraImage: CameraImageResponse;
}

export const CandidateDetail: React.FC<CandidateDetailProps> = ({ cameraImage }) => {
  const [searchParams] = useSearchParams();
  const { imageId } = useParams<{ imageId: string }>();
  const initialUpc = searchParams.get('upc');

  const navigate = useNavigate();

  const [selectionState, dispatchSelection] = useReducer(selectionReducer, {
    highlightedPogBox: null,
    selectedBoxes: new Set<string>(),
    filterType: 'all' as ClassificationType | 'all',
    pendingSaves: new Map<number, PendingSave>(),
  });
  const { selectedBoxes, filterType, highlightedPogBox, pendingSaves } = selectionState;
  const { token, legacyToken } = useAuthContext();
  const { showErrorToast, showSuccessToast } = useNotificationContext();
  const [isLoadingNavigation, setIsLoadingNavigation] = useState(false);
  const [cameras, setCameras] = useState<CameraDetails[]>([]);
  const [showShelfColumnLabels, setShowShelfColumnLabels] = useState(false);
  const [showEmbeddingsCount, setShowEmbeddingsCount] = useState(true);
  const [enabledMods, setEnabledMods] = useState<Set<string>>(new Set());
  const [isReviewMode, setIsReviewMode] = useState(false);
  const [showCreateConfirmation, setShowCreateConfirmation] = useState(false);
  const [designsByItemNumber, setDesignsByItemNumber] = useState<Record<string, DesignResponse[]>>(
    {}
  );
  const [isLoadingDesigns, setIsLoadingDesigns] = useState(false);
  const [designsLoadFailed, setDesignsLoadFailed] = useState(false);
  const [isSavingEmbeddings, setIsSavingEmbeddings] = useState(false);
  const [newDesignIntents, setNewDesignIntents] = useState<Map<string, NewDesignIntent>>(new Map());
  const [variantModal, setVariantModal] = useState<{
    productCropScanId: number;
    variants: ProductVariantResponse[];
    loading: boolean;
  } | null>(null);
  const page = searchParams.get('page');

  const chainId = cameraImage.chainId;
  const storeId = cameraImage.storeId;
  const storeName = cameraImage.storeName;
  const cameraId = cameraImage.cameraId;

  const { getTimeZone } = useStoreTimeZones();
  const storeTimeZone = getTimeZone(storeId, chainId);
  const initialBbox = useMemo(() => {
    const box = searchParams.get('box')?.split(',')?.map(Number);

    return box?.length === 4 ? (box as Rect) : undefined;
  }, [searchParams]);

  // Use the product recognition scanning hook
  const {
    cropScans,
    cropScansImageUrl,
    isFetchingCropScans,
    isGeneratingCropScans,
    cropScansError,
    sessionStarted,
    startSession,
  } = useProductRecognitionScanning(imageId || '');

  // Track previous sessionStarted value to detect false -> true transitions
  const prevSessionStarted = useRef(sessionStarted);

  const blocker = useBlocker(pendingSaves.size > 0);

  const fetchCameras = useCallback(async () => {
    if (!storeName) {
      return null;
    }

    try {
      const data = await ApiService.fetchCameras(token, storeName, {
        useCache: true,
      });
      setCameras(data);
    } catch (err) {
      showErrorToast('Failed to fetch cameras');
      return null;
    }
  }, [token, storeName, showErrorToast]);

  // Get current image from cameraImage
  const currentImage = cameraImage.image;

  const cameraTimeline = useMemo(
    () => ({
      images: [cameraImage.image],
      templates: cameraImage.templates,
      pogs: cameraImage.pogs,
    }),
    [cameraImage]
  );

  // Use the image preloader hook
  const { preloadedImagesB64, isLoading: isLoadingImages } = useImagePreloader({
    cameraTimeline,
    authToken: legacyToken,
    pogImageUri: undefined,
  });

  // Find the selected camera based on cameraId from query params
  const selectedCamera = useMemo(() => {
    if (!cameraId || !cameras || cameras.length === 0) {
      return null;
    }
    return cameras.find((cam) => cam.cameraId === cameraId) ?? null;
  }, [cameraId, cameras]);

  const allTagNames = useMemo(() => {
    if (!selectedCamera) return [];
    return selectedCamera.mods.map((mod) => mod.tagName);
  }, [selectedCamera]);

  useEffect(() => {
    setEnabledMods(new Set(allTagNames));
  }, [allTagNames]);

  const toggleMod = useCallback((tagName: string) => {
    setEnabledMods((prev) => {
      const next = new Set(prev);
      if (next.has(tagName)) {
        next.delete(tagName);
      } else {
        next.add(tagName);
      }
      return next;
    });
  }, []);

  // Get POG data for the current image based on il_section_ids
  const currentPogs = useMemo(() => {
    const allPogs = cameraImage.pogs ? Object.values(cameraImage.pogs) : [];

    return allPogs
      .filter((pog): pog is CameraPog => pog !== undefined)
      .sort((a, b) => {
        if (!a || !b) return 0;
        return allTagNames.indexOf(a.tagName) - allTagNames.indexOf(b.tagName);
      });
  }, [cameraImage, allTagNames]);

  const filteredPogs = useMemo(() => {
    return currentPogs.filter((pog) => enabledMods.size === 0 || enabledMods.has(pog.tagName));
  }, [currentPogs, enabledMods]);

  // Precompute POG previews by product_id so we can quickly show the matching POG image per crop scan
  const pogPreviewsByProductId = useMemo(() => {
    if (!currentPogs || currentPogs.length === 0)
      return {} as Record<
        number,
        {
          pogBox: PogBBox;
        }
      >;

    const previews: Record<
      number,
      {
        pogBox: PogBBox;
      }
    > = {};

    currentPogs.forEach((pog) => {
      (pog.bboxes ?? []).forEach((bbox) => {
        const productId = bbox.productId;
        if (!productId || previews[productId]) {
          return;
        }

        previews[productId] = {
          pogBox: bbox,
        };
      });
    });

    return previews;
  }, [currentPogs]);

  // Filter crop scans based on classification type
  const filteredCropScans = useMemo(() => {
    const filteredScans = cropScans
      .filter(
        (scan) => isReviewMode || filterType === 'all' || scan.classification_type === filterType
      )
      .map((scan) => {
        const pendingSave = pendingSaves.get(scan.product_crop_scan_id);

        // Get shelf-column label from pendingSave or by looking up completed scan
        let shelfColumnLabel: string | undefined;
        if (pendingSave?.shelfColumnLabel) {
          shelfColumnLabel = pendingSave.shelfColumnLabel;
        } else if (scan.product_id && currentPogs.length > 0) {
          // For completed scans, find matching POG box by product id and build label
          for (let pogIndex = 0; pogIndex < currentPogs.length; pogIndex++) {
            const pog = currentPogs[pogIndex];
            const matchingBox = pog?.bboxes.find((box) => box.productId === scan.product_id);
            if (
              matchingBox &&
              matchingBox.shelf !== undefined &&
              matchingBox.column !== undefined
            ) {
              // Only include mod number if there are multiple POGs
              shelfColumnLabel =
                currentPogs.length > 1
                  ? `${pogIndex + 1}-${matchingBox.shelf}-${matchingBox.column}`
                  : `${matchingBox.shelf}-${matchingBox.column}`;
              break;
            }
          }
        }

        if (!pendingSave) {
          return { ...scan, isPending: false, shelfColumnLabel };
        }

        const realDesigns = pendingSave.itemNumber
          ? (designsByItemNumber[pendingSave.itemNumber] ?? [])
          : [];
        const groupKey = getIntentGroupKey(pendingSave);
        const matchingIntents = Array.from(newDesignIntents.values()).filter(
          (i) => i.groupKey === groupKey
        );
        const syntheticDesigns: DesignResponse[] = matchingIntents.map(
          (intent) =>
            ({
              _id: intent.intentId,
              chainId: Number(chainId),
              barcodeStatus: 'unconfirmed',
              normalizedBarcode: intent.variantUpc,
              productName: intent.variantName ?? undefined,
              ...({
                __isIntent: true,
                __intentBbox: intent.originBbox,
                itemNumber: pendingSave.itemNumber,
              } as Partial<DesignResponse>),
            }) as DesignResponse
        );

        return {
          ...scan,
          product_id: pendingSave.productId ?? undefined,
          product_name: pendingSave.productName ?? undefined,
          barcode: pendingSave.upc,
          isPending: true,
          shelfColumnLabel,
          orientation: pendingSave.orientation,
          designs: [...realDesigns, ...syntheticDesigns],
          selectedDesignId: pendingSave.selectedDesignId,
          itemNumber: pendingSave.itemNumber,
        };
      })
      .sort((a, b) => {
        return Number(b.isPending) - Number(a.isPending);
      });
    return filteredScans;
  }, [
    cropScans,
    filterType,
    pendingSaves,
    currentPogs,
    isReviewMode,
    designsByItemNumber,
    newDesignIntents,
    chainId,
  ]);

  const sortedByTypeCropScans = useMemo(() => {
    if (filterType !== 'all') {
      return filteredCropScans;
    }

    return [...filteredCropScans].sort((a, b) => {
      if (a.classification_type === 'product' && b.classification_type !== 'product') {
        return 1;
      }
      if (a.classification_type !== 'product' && b.classification_type === 'product') {
        return -1;
      }
      return 0;
    });
  }, [filteredCropScans, filterType]);

  const selectedIlItemIds = useMemo(() => {
    const selectedIds = new Set<number>();

    // Add POG boxes that have ilItemIds in pendingSaves
    pendingSaves.forEach((pogData) => {
      selectedIds.add(pogData.ilItemId);
    });

    cropScans
      .filter((scan) => scan.normalized_barcode || scan.barcode)
      .forEach((scan) => {
        currentPogs.forEach((pog) => {
          pog?.bboxes.forEach((box) => {
            if (box.productId === scan.product_id) {
              selectedIds.add(box.ilItemId);
            }
          });
        });
      });

    return selectedIds;
  }, [cropScans, currentPogs, pendingSaves]);

  const fetchCandidate = useCallback(
    async (params: QueryParams) => {
      if (!token || !chainId || !initialUpc) {
        return null;
      }

      try {
        const apiParams: CandidatesSearchParams = {
          chainId: chainId,
          upc: initialUpc,
          ...(storeId && {
            storeId: storeId,
          }),
          page: params.page + 1, // API uses 1-based pagination
          limit: params.pageSize,
        };

        const data = await ApiService.searchCandidates(token, apiParams);

        return data[0];
      } catch (err) {
        return null;
      }
    },
    [token, chainId, initialUpc, storeId]
  );

  // Create filtered bboxes from crop scans
  const cropScansFilteredBboxes = useMemo(() => {
    if (!sortedByTypeCropScans || sortedByTypeCropScans.length === 0) return [];

    return sortedByTypeCropScans.map((cropScan) => {
      const boxId = cropScan.product_crop_scan_id.toString();
      const isSelected = selectedBoxes.has(boxId);

      const [x1, y1, width, height] = cropScan.bbox;
      const x2 = x1 + width;
      const y2 = y1 + height;
      const isCompleted = !!(cropScan.barcode || cropScan.normalized_barcode);

      // Get shelf-column label - first check pendingSaves, then look up from POG if already completed
      let shelfColumnLabel: string | undefined;
      const pendingSave = pendingSaves.get(cropScan.product_crop_scan_id);

      if (pendingSave?.shelfColumnLabel) {
        // Use label from pending save
        shelfColumnLabel = pendingSave.shelfColumnLabel;
      } else if (isCompleted && currentPogs.length > 0) {
        // For completed scans, find matching POG box by UPC and build label
        for (let pogIndex = 0; pogIndex < currentPogs.length; pogIndex++) {
          const pog = currentPogs[pogIndex];
          const matchingBox = pog?.bboxes.find((box) => box.productId === cropScan.product_id);
          if (matchingBox && matchingBox.shelf !== undefined && matchingBox.column !== undefined) {
            // Only include mod number if there are multiple POGs
            shelfColumnLabel =
              currentPogs.length > 1
                ? `${pogIndex + 1}-${matchingBox.shelf}-${matchingBox.column}`
                : `${matchingBox.shelf}-${matchingBox.column}`;
            break;
          }
        }
      }

      return {
        bboxId: cropScan.product_crop_scan_id,
        tagName: `crop_scan_${cropScan.product_crop_scan_id}`,
        x1,
        y1,
        x2,
        y2,
        productId: cropScan.product_id ?? 0,
        itemNumber: '0',
        upc: cropScan.normalized_barcode ?? '',
        name: null,
        imageUri: null,
        modCoverageId: 0,
        templateId: 0,
        color: isSelected
          ? COLORS.SELECTED_BOX
          : isCompleted
            ? COLORS.UPDATED_BOX
            : COLORS.UNSELECTED_BOX,
        // Add the complete label
        shelfColumnLabel,
      };
    });
  }, [selectedBoxes, sortedByTypeCropScans, pendingSaves, currentPogs]);

  const selectedCropScans = useMemo(() => {
    return filteredCropScans.filter((cropScan) => {
      const boxId = cropScan.product_crop_scan_id.toString();
      return selectedBoxes.has(boxId);
    });
  }, [filteredCropScans, selectedBoxes]);

  const handleFilterChange = useCallback(
    (value: ClassificationType | 'all') => {
      dispatchSelection({
        type: 'SET_FILTER_TYPE',
        payload: value,
      });
    },
    [dispatchSelection]
  );

  const handleBoxClick = (bbox: BBox) => {
    dispatchSelection({
      type: 'TOGGLE_SELECTED_BOX',
      payload: bbox.bboxId.toString(),
    });
  };

  const clearCurrentSelections = useCallback(() => {
    dispatchSelection({ type: 'SET_HIGHLIGHTED_POG_BOX', payload: null });
    dispatchSelection({ type: 'CLEAR_SELECTED_BOXES' });
  }, []);

  const handlePogBoxClick = useCallback(
    (pogBox: PogBBox) => {
      if (selectedCropScans.length === 0) {
        showErrorToast('Please select crop scans first to match with product.');
        return;
      }

      if (pogBox.ilItemId) {
        // Build the shelf-column label with mod index
        let shelfColumnLabel: string | undefined;
        if (pogBox.shelf !== undefined && pogBox.column !== undefined && currentPogs.length > 0) {
          // Find which POG (mod) this box belongs to
          const modIndex = currentPogs.findIndex((pog) =>
            pog?.bboxes.some((box) => box.ilItemId === pogBox.ilItemId)
          );
          if (modIndex !== -1) {
            // Only include mod number if there are multiple POGs
            shelfColumnLabel =
              currentPogs.length > 1
                ? `${modIndex + 1}-${pogBox.shelf}-${pogBox.column}`
                : `${pogBox.shelf}-${pogBox.column}`;
          }
        }

        // Store the selection for later batch saving
        dispatchSelection({
          type: 'ADD_PENDING_SAVES',
          payload: {
            cropScans: selectedCropScans,
            pogBox: {
              upc: pogBox.upc,
              ilItemId: pogBox.ilItemId,
              itemNumber: pogBox.itemNumber,
              productName: pogBox.name,
              productId: pogBox.productId,
              shelfColumnLabel,
            },
          },
        });

        // If user is in review mode, exit it so designs get re-fetched on next entry.
        if (isReviewMode) {
          setIsReviewMode(false);
          setDesignsLoadFailed(false);
          setNewDesignIntents(new Map());
        }

        clearCurrentSelections();

        // Show success message
        const productInfo = [
          `UPC: ${pogBox.upc}`,
          pogBox.productId ? `Product ID: ${pogBox.productId}` : null,
          pogBox.name ? `Name: ${pogBox.name}` : null,
        ]
          .filter(Boolean)
          .join(', ');

        showSuccessToast(
          `${selectedCropScans.length} crop scan${
            selectedCropScans.length > 1 ? 's' : ''
          } matched with ${productInfo}`
        );
      }
    },
    [
      selectedCropScans,
      showErrorToast,
      dispatchSelection,
      clearCurrentSelections,
      showSuccessToast,
      currentPogs,
      isReviewMode,
    ]
  );

  // Find POG box that matches the UPC from URL params
  const pogBoxMatchingUpc = useMemo(() => {
    if (!initialUpc || !currentPogs || currentPogs.length === 0) return null;

    // Find POG box that matches the UPC
    for (const pog of currentPogs) {
      if (pog?.bboxes) {
        const matchingBox = pog.bboxes.find((box: PogBBox) => box.upc === initialUpc);
        if (matchingBox) {
          return matchingBox;
        }
      }
    }
    return null;
  }, [initialUpc, currentPogs]);

  useEffect(() => {
    // Only trigger when sessionStarted changes from false to true
    const sessionJustStarted = !prevSessionStarted.current && sessionStarted;

    if (!sessionJustStarted || !initialBbox) {
      return;
    }

    if (pogBoxMatchingUpc) {
      dispatchSelection({
        type: 'SET_HIGHLIGHTED_POG_BOX',
        payload: pogBoxMatchingUpc,
      });
    }

    if (filteredCropScans.length === 0) {
      return;
    }

    prevSessionStarted.current = sessionStarted;

    const overlappingCropScans = new Set<string>();

    filteredCropScans.forEach((cropScan) => {
      if (cropScan.normalized_barcode || cropScan.barcode) {
        return;
      }

      if (areBoxOverlapping(initialBbox, cropScan.bbox, OVERLAP_THRESHOLD)) {
        overlappingCropScans.add(cropScan.product_crop_scan_id.toString());
      }
    });

    dispatchSelection({
      type: 'SET_SELECTED_BOXES',
      payload: overlappingCropScans,
    });
  }, [filteredCropScans, initialBbox, initialUpc, pogBoxMatchingUpc, sessionStarted]);

  // Clear selected boxes when session ends
  useEffect(() => {
    if (!sessionStarted) {
      dispatchSelection({ type: 'CLEAR_SELECTED_BOXES' });
    }
  }, [sessionStarted]);

  const handleToggleOrientation = useCallback(
    (productCropScanId: number) => {
      const existing = pendingSaves.get(productCropScanId);
      if (!existing) return;
      dispatchSelection({
        type: 'SET_PENDING_ORIENTATION',
        payload: {
          productCropScanId,
          orientation: existing.orientation === 'front' ? 'not-front' : 'front',
        },
      });
    },
    [pendingSaves]
  );

  const handleSelectDesign = useCallback(
    async (productCropScanId: number, designId: string | 'new') => {
      const existing = pendingSaves.get(productCropScanId);
      if (!existing) return;

      if (existing.selectedDesignId === designId) {
        dispatchSelection({
          type: 'SET_PENDING_SELECTED_DESIGN',
          payload: { productCropScanId, selectedDesignId: undefined },
        });
        return;
      }

      if (designId === 'new') {
        if (!token || !storeName) {
          showErrorToast('Cannot fetch variants: missing auth or store');
          return;
        }
        setVariantModal({ productCropScanId, variants: [], loading: true });
        try {
          const variants = await ApiService.fetchVariants(token, storeName, existing.upc);
          setVariantModal({ productCropScanId, variants, loading: false });
        } catch (err) {
          setVariantModal(null);
          showErrorToast('Failed to fetch variants');
        }
        return;
      }

      const resolvedId: string | undefined = designId;
      dispatchSelection({
        type: 'SET_PENDING_SELECTED_DESIGN',
        payload: { productCropScanId, selectedDesignId: resolvedId },
      });
    },
    [pendingSaves, token, storeName, showErrorToast]
  );

  const handleVariantSelected = useCallback(
    (variant: ProductVariantResponse) => {
      const productCropScanId = variantModal?.productCropScanId;
      if (productCropScanId === undefined) return;
      setVariantModal(null);

      const existing = pendingSaves.get(productCropScanId);
      if (!existing) return;

      const cropScan = cropScans.find((c) => c.product_crop_scan_id === productCropScanId);
      if (!cropScan) return;

      const groupKey = getIntentGroupKey(existing);
      const intentId = `new:${groupKey}:${Date.now()}`;
      // If this crop's previous selection was an intent that *this same crop*
      // originated, drop it — re-clicking "+ New design" replaces rather than
      // duplicates. Any sibling crops that were sharing that intent get their
      // selection cleared (they'll need to pick again). Edge case; consistent
      // data wins over polish.
      const priorIntentId = existing.selectedDesignId;
      const priorIntent = priorIntentId ? newDesignIntents.get(priorIntentId) : undefined;
      const droppedIntentId =
        priorIntent && priorIntent.originScanId === productCropScanId ? priorIntent.intentId : null;

      setNewDesignIntents((m) => {
        const next = new Map(m);
        if (droppedIntentId) next.delete(droppedIntentId);
        next.set(intentId, {
          intentId,
          groupKey,
          originScanId: productCropScanId,
          originBbox: cropScan.bbox as [number, number, number, number],
          variantUpc: variant.upc,
          variantName: variant.name,
        });
        return next;
      });

      if (droppedIntentId) {
        // Unbind any sibling crops that were pointing at the dropped intent.
        pendingSaves.forEach((p, scanId) => {
          if (scanId !== productCropScanId && p.selectedDesignId === droppedIntentId) {
            dispatchSelection({
              type: 'SET_PENDING_SELECTED_DESIGN',
              payload: { productCropScanId: scanId, selectedDesignId: undefined },
            });
          }
        });
      }

      dispatchSelection({
        type: 'SET_PENDING_SELECTED_DESIGN',
        payload: { productCropScanId, selectedDesignId: intentId },
      });
    },
    [variantModal, pendingSaves, cropScans, newDesignIntents]
  );

  const handleDeleteCropScan = useCallback(
    async (productCropScanId: number) => {
      if (!pendingSaves.has(productCropScanId)) {
        return;
      }

      dispatchSelection({
        type: 'REMOVE_PENDING_SAVE',
        payload: productCropScanId,
      });
    },
    [pendingSaves]
  );

  const handleEnterReviewMode = useCallback(async () => {
    setIsReviewMode(true);

    if (!token || !chainId || pendingSaves.size === 0) {
      return;
    }

    const itemNumbers = new Set<string>();
    const normalizedBarcodes = new Set<string>();

    pendingSaves.forEach((pending) => {
      if (pending.itemNumber) {
        itemNumbers.add(pending.itemNumber);
      } else if (pending.upc) {
        normalizedBarcodes.add(pending.upc);
      }
    });

    const limit = 100;
    const baseParams = {
      chainId,
      storeName,
      itemNumbers: itemNumbers.size > 0 ? [...itemNumbers] : undefined,
      normalizedBarcodes: normalizedBarcodes.size > 0 ? [...normalizedBarcodes] : undefined,
      limit,
    };

    setIsLoadingDesigns(true);
    setDesignsLoadFailed(false);
    try {
      const firstPage = await ApiService.searchDesigns(token, { ...baseParams, page: 1 });
      const allResults = [...firstPage.results];
      const totalPages = Math.ceil(firstPage.total / limit);

      if (totalPages > 1) {
        const remainingPages = await Promise.all(
          Array.from({ length: totalPages - 1 }, (_, i) =>
            ApiService.searchDesigns(token, { ...baseParams, page: i + 2 })
          )
        );
        remainingPages.forEach((p) => allResults.push(...p.results));
      }

      const grouped: Record<string, DesignResponse[]> = {};
      allResults.forEach((design) => {
        if (!design.itemNumber) return;
        if (!grouped[design.itemNumber]) grouped[design.itemNumber] = [];
        grouped[design.itemNumber].push(design);
      });
      setDesignsByItemNumber(grouped);

      if (getAutoAssignSingleDesign(Number(chainId))) {
        pendingSaves.forEach((pending, productCropScanId) => {
          if (pending.selectedDesignId || !pending.itemNumber) return;
          const designs = grouped[pending.itemNumber];
          if (designs?.length === 1) {
            dispatchSelection({
              type: 'SET_PENDING_SELECTED_DESIGN',
              payload: { productCropScanId, selectedDesignId: designs[0]._id },
            });
          }
        });
      }
    } catch (err) {
      setDesignsLoadFailed(true);
    } finally {
      setIsLoadingDesigns(false);
    }
  }, [token, chainId, storeName, pendingSaves]);

  // Function to handle saving selected crop scans
  const handleCreateEmbeddings = useCallback(async () => {
    if (!token || !imageId || !storeName) {
      showErrorToast('Cannot save: missing auth/image/store');
      return;
    }

    const designById = new Map<string, DesignResponse>();
    Object.values(designsByItemNumber).forEach((list) => {
      list.forEach((design) => designById.set(design._id, design));
    });

    const entries = Array.from(pendingSaves.entries());

    const cropScanById = new Map(cropScans.map((c) => [c.product_crop_scan_id, c]));
    const isCarton = (productCropScanId: number) =>
      cropScanById.get(productCropScanId)?.classification_type === 'carton';

    // A pendingSave's selectedDesignId is either a real design id or an intent id.
    const isIntent = (id?: string): id is string => Boolean(id && newDesignIntents.has(id));

    // 1) Build initial create-embeddings payload. For pendingSaves bound to an
    //    intent, omit design_id (intent's design doesn't exist yet — we'll
    //    create it after step 2). Carton crops never get a design assignment.
    const initialUpdates = entries.map(([productCropScanId, pogData]) => {
      const carton = isCarton(productCropScanId);
      const intent = isIntent(pogData.selectedDesignId)
        ? newDesignIntents.get(pogData.selectedDesignId!)
        : undefined;
      const selectedDesign =
        pogData.selectedDesignId && !intent ? designById.get(pogData.selectedDesignId) : undefined;
      const normalizedBarcode = intent
        ? intent.variantUpc
        : (selectedDesign?.normalizedBarcode ?? pogData.upc);
      return {
        product_crop_scan_id: productCropScanId,
        normalized_barcode: normalizedBarcode,
        product_id: pogData.productId,
        orientation: pogData.orientation,
        ...(!carton && pogData.selectedDesignId && !intent
          ? { design_id: pogData.selectedDesignId }
          : {}),
      };
    });

    setIsSavingEmbeddings(true);
    try {
      // 2) Create embeddings → returns mapping productCropScanId → productEmbeddingCropId.
      const embeddingMapping = await ApiService.createEmbeddings(token, imageId, initialUpdates);
      const embeddingCropIdByScanId = new Map<number, string>();
      embeddingMapping.forEach((m) => {
        embeddingCropIdByScanId.set(m.product_crop_scan_id, m.product_embedding_crop_id);
      });

      // 3) Create one design per intent that has at least one non-carton crop bound to it.
      const newDesignIdByIntent: Record<string, string> = {};
      const newByIntent = new Map<string, { productCropScanId: number; pogData: PendingSave }[]>();
      entries.forEach(([productCropScanId, pogData]) => {
        if (isCarton(productCropScanId)) return;
        if (!isIntent(pogData.selectedDesignId)) return;
        const intentId = pogData.selectedDesignId!;
        const list = newByIntent.get(intentId) ?? [];
        list.push({ productCropScanId, pogData });
        newByIntent.set(intentId, list);
      });

      await Promise.all(
        Array.from(newByIntent.entries()).map(async ([intentId, scans]) => {
          const intent = newDesignIntents.get(intentId);
          if (!intent) return;
          const first = scans[0];
          const embeddingCropId = embeddingCropIdByScanId.get(first.productCropScanId);
          if (!embeddingCropId) return;
          try {
            const design = await ApiService.createEmptyDesign(token, {
              storeName,
              barcode: intent.variantUpc,
              representativeCropId: embeddingCropId,
            });
            newDesignIdByIntent[intentId] = design._id;
          } catch (err) {
            // Non-fatal: crops bound to this intent will save without design_id.
            // eslint-disable-next-line no-console
            console.error('createEmptyDesign failed for intent', intentId, err);
          }
        })
      );

      // 4) PATCH crop-scans with the now-resolved design_ids. Carton crops
      //    never get a design_id.
      const finalUpdates = entries.map(([productCropScanId, pogData]) => {
        const carton = isCarton(productCropScanId);
        const intent = isIntent(pogData.selectedDesignId)
          ? newDesignIntents.get(pogData.selectedDesignId!)
          : undefined;
        const resolvedDesignId = carton
          ? undefined
          : intent
            ? newDesignIdByIntent[pogData.selectedDesignId!]
            : pogData.selectedDesignId;
        const selectedDesign =
          resolvedDesignId && !intent ? designById.get(resolvedDesignId) : undefined;
        const normalizedBarcode = intent
          ? intent.variantUpc
          : (selectedDesign?.normalizedBarcode ?? pogData.upc);
        return {
          product_crop_scan_id: productCropScanId,
          normalized_barcode: normalizedBarcode,
          product_id: pogData.productId,
          orientation: pogData.orientation,
          ...(resolvedDesignId ? { design_id: resolvedDesignId } : {}),
        };
      });

      try {
        await ApiService.updateProductRecognitionCropScans(token, Number(imageId), finalUpdates);
      } catch (err) {
        // Non-fatal: design_ids on crop scans won't be persisted, but the
        // embeddings themselves were created in step 2.
        // eslint-disable-next-line no-console
        console.error('updateProductRecognitionCropScans failed', err);
      }

      // 5) Assign embedding crops to each newly-created design.
      await Promise.all(
        Array.from(newByIntent.entries()).map(async ([intentId, scans]) => {
          const designId = newDesignIdByIntent[intentId];
          if (!designId) return;
          const cropIds = scans
            .map(({ productCropScanId }) => embeddingCropIdByScanId.get(productCropScanId))
            .filter((id): id is string => Boolean(id));
          if (cropIds.length === 0) return;
          try {
            await ApiService.assignDesignCrops(token, { cropIds, designId });
          } catch (err) {
            // Non-fatal: design exists but crops aren't linked.
            // eslint-disable-next-line no-console
            console.error('assignDesignCrops failed for design', designId, err);
          }
        })
      );

      showSuccessToast(`Created ${finalUpdates.length} embeddings`);
      dispatchSelection({ type: 'CLEAR_PENDING_SAVES' });
      setNewDesignIntents(new Map());
      setIsReviewMode(false);
      startSession();
    } catch (err) {
      showErrorToast('Failed to create embeddings');
    } finally {
      setIsSavingEmbeddings(false);
    }
  }, [
    token,
    imageId,
    storeName,
    pendingSaves,
    cropScans,
    designsByItemNumber,
    newDesignIntents,
    startSession,
    showSuccessToast,
    showErrorToast,
  ]);

  const goToPage = useCallback(
    async (pageNumber: number) => {
      setIsLoadingNavigation(true);

      try {
        const nextCandidate = await fetchCandidate({
          page: pageNumber,
          pageSize: 1,
        });

        if (!nextCandidate) {
          showErrorToast('Failed to fetch next image');
          return;
        }

        const newImageId = nextCandidate.imageId;

        // Navigate to the same route with new imageId, replacing current entry to reset all state
        const currentParams = new URLSearchParams(searchParams);
        currentParams.set('image_id', newImageId.toString());
        currentParams.set('box', nextCandidate.bboxCoords.join(','));
        currentParams.set('page', pageNumber.toString());
        currentParams.set('refresh', Date.now().toString()); // Force component remount
        navigate(`?${currentParams.toString()}`, { replace: true });
      } catch {
        showErrorToast('Failed to fetch next image');
      } finally {
        setIsLoadingNavigation(false);
      }
    },
    [fetchCandidate, showErrorToast, searchParams, navigate, setIsLoadingNavigation]
  );

  const titleActions = useMemo(
    () => (
      <div className="flex items-center gap-4">
        <FormControlLabel
          control={
            <Switch
              checked={showShelfColumnLabels}
              onChange={(e) => setShowShelfColumnLabels(e.target.checked)}
              size="small"
            />
          }
          label="Show Labels"
        />
        <FormControlLabel
          control={
            <Switch
              checked={showEmbeddingsCount}
              onChange={(e) => setShowEmbeddingsCount(e.target.checked)}
              size="small"
            />
          }
          label="Count Embeddings"
        />
        <EmbeddingTypeFilter value={filterType} onChange={handleFilterChange} />
      </div>
    ),
    [filterType, handleFilterChange, showShelfColumnLabels, showEmbeddingsCount]
  );

  const pogHeaderActions = useMemo(
    () =>
      allTagNames.length > 0 ? (
        <ModFilters allTagNames={allTagNames} enabledMods={enabledMods} toggleMod={toggleMod} />
      ) : undefined,
    [allTagNames, enabledMods, toggleMod]
  );

  const handleBack = useCallback(() => navigate(-1), [navigate]);

  const handlePreviousImage = useCallback(() => {
    if (page && parseInt(page) > 0) {
      goToPage(parseInt(page) - 1);
    }
  }, [page, goToPage]);

  const handleNextImage = useCallback(() => {
    if (page) {
      goToPage(parseInt(page) + 1);
    }
  }, [page, goToPage]);

  const handleAcceptSelection = useCallback(() => {
    if (highlightedPogBox) {
      handlePogBoxClick(highlightedPogBox);
    }
  }, [highlightedPogBox, handlePogBoxClick]);

  const handleBoxesSelected = useCallback(
    (selectedBboxes: BBox[]) => {
      // Convert selected BBox objects to their IDs
      const newSelectedIds = selectedBboxes.map((bbox) => bbox.bboxId.toString());

      // Merge with existing selected boxes
      const mergedSelectedBoxIds = new Set([
        ...selectedBoxes, // existing selected boxes
        ...newSelectedIds, // newly selected boxes
      ]);

      dispatchSelection({
        type: 'SET_SELECTED_BOXES',
        payload: mergedSelectedBoxIds,
      });
    },
    [dispatchSelection, selectedBoxes]
  );

  useEffect(() => {
    startSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraImage]);

  useEffect(() => {
    fetchCameras();
  }, [fetchCameras]);

  if (!imageId || !parseInt(imageId, 10)) {
    return null;
  }

  const preloadedImage = currentImage ? preloadedImagesB64[currentImage.imageUri] : undefined;

  return (
    <div className="h-screen bg-gray-50 p-4 flex flex-col">
      <div className="w-full mx-auto space-y-4 flex flex-col flex-1 min-h-0">
        <CandidateDetailHeader
          detectedAt={currentImage?.detectedAt}
          storeTimeZone={storeTimeZone}
          storeName={storeName ?? null}
          upc={initialUpc}
          imageId={imageId}
          onBack={handleBack}
          page={page}
          showNavigation={!!initialUpc}
          isLoadingNavigation={isLoadingNavigation}
          onPreviousImage={handlePreviousImage}
          onNextImage={handleNextImage}
          showProductSelection={!!highlightedPogBox?.ilItemId && selectedCropScans.length > 0}
          onRejectSelection={clearCurrentSelections}
          onAcceptSelection={handleAcceptSelection}
        />

        <UnsavedChangesDialog
          open={blocker.state === 'blocked'}
          onCancel={() => blocker.reset?.()}
          onDiscard={() => blocker.proceed?.()}
        />

        <VariantSelectionModal
          open={variantModal !== null}
          loading={variantModal?.loading ?? false}
          variants={variantModal?.variants ?? []}
          token={token}
          chainId={chainId ? Number(chainId) : undefined}
          onSelect={handleVariantSelected}
          onClose={() => setVariantModal(null)}
        />

        {/* Image Viewer */}
        {preloadedImage && (
          <div className="bg-white rounded-lg shadow-md p-6 flex-1">
            {/* Image Display */}
            <div className="mb-4">
              <CameraImageViewer
                highlightedIlItemId={highlightedPogBox?.ilItemId}
                preloadedImage={preloadedImage}
                filteredBboxes={cropScansFilteredBboxes}
                boxActions={[]}
                productRecognitionDetections={[]}
                planogramComplianceActions={[]}
                showProductImages={false}
                onBoxClick={handleBoxClick}
                onBoxesSelected={handleBoxesSelected}
                titleActions={titleActions}
                currentPogs={filteredPogs}
                preloadedImagesB64={preloadedImagesB64}
                selectedIlItemIds={selectedIlItemIds}
                fallbackPogBoxColor={COLORS.UNSELECTED_BOX}
                selectedPogBoxColor={COLORS.UPDATED_BOX}
                planogramStatusMap={new Map()}
                onPogBoxClick={handlePogBoxClick}
                showPogs
                showShelfColumnLabels={showShelfColumnLabels}
                showEmbeddingsCount={showEmbeddingsCount}
                pogHeaderActions={pogHeaderActions}
                showFallbackImages={false}
                chainId={chainId}
              />
            </div>

            {/* Full-width section */}
            <div className="mt-4">
              {/* Selected Boxes Info */}
              <div>
                {selectedBoxes.size > 0 && (
                  <div className="mb-4 p-3 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-center justify-between">
                      <span className="text-green-800 font-medium">
                        {selectedBoxes.size} box
                        {selectedBoxes.size !== 1 ? 'es' : ''} selected
                      </span>
                      <button
                        onClick={() => dispatchSelection({ type: 'CLEAR_SELECTED_BOXES' })}
                        className="px-2 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                      >
                        Clear Selection
                      </button>
                    </div>
                  </div>
                )}
                {/* Product Recognition Crop Scans */}
                <div className="p-4 bg-blue-50 rounded-lg">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="font-medium text-blue-900 mb-1">
                        Product Recognition Crop Scans
                      </h3>
                      {isReviewMode && !isLoadingDesigns && (
                        <p className="text-sm text-blue-800">
                          {designsLoadFailed
                            ? 'Designs failed to load. For each crop scan, set Front / Non-front.'
                            : 'For each crop scan, select an existing design (or "Add new design") and set Front / Non-front.'}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {sessionStarted && (
                        <button
                          onClick={() => {
                            if (isReviewMode) {
                              setIsReviewMode(false);
                              setDesignsLoadFailed(false);
                            } else {
                              handleEnterReviewMode();
                            }
                          }}
                          disabled={
                            !isReviewMode && !filteredCropScans.some((scan) => scan.isPending)
                          }
                          className={`px-3 py-2 text-sm rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                            isReviewMode
                              ? 'bg-blue-800 text-white hover:bg-blue-900 focus:ring-blue-500'
                              : 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500'
                          }`}
                        >
                          {isReviewMode ? 'Show all crop scans' : 'Review assigned embeddings'}
                        </button>
                      )}
                    </div>
                  </div>

                  {cropScansError && (
                    <div className="text-red-600 text-sm mb-3 p-2 bg-red-50 rounded border">
                      Error: {cropScansError.message}
                    </div>
                  )}

                  {sessionStarted ? (
                    <>
                      <CropScansList
                        cropScans={filteredCropScans}
                        imageUrl={cropScansImageUrl}
                        imageId={imageId ? Number(imageId) : undefined}
                        isFetchingCropScans={isFetchingCropScans || isLoadingDesigns}
                        isGeneratingCropScans={isGeneratingCropScans}
                        pendingOnly={isReviewMode}
                        previewSize={isReviewMode ? 250 : undefined}
                        onDelete={handleDeleteCropScan}
                        onToggleOrientation={handleToggleOrientation}
                        onSelectDesign={
                          isReviewMode && !designsLoadFailed ? handleSelectDesign : undefined
                        }
                        selectedBoxes={selectedBoxes}
                        pogPreviewsByProductId={pogPreviewsByProductId}
                      />
                      {isReviewMode && (
                        <div className="mt-4 flex justify-end">
                          <button
                            onClick={() => setShowCreateConfirmation(true)}
                            disabled={
                              isFetchingCropScans ||
                              isGeneratingCropScans ||
                              isLoadingDesigns ||
                              isSavingEmbeddings ||
                              pendingSaves.size === 0 ||
                              !storeName
                            }
                            className="px-3 py-2 bg-purple-600 text-white text-sm rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isFetchingCropScans || isSavingEmbeddings
                              ? 'Saving...'
                              : `Create embeddings (${pendingSaves.size})`}
                          </button>
                        </div>
                      )}

                      <Dialog
                        open={showCreateConfirmation}
                        onClose={() => setShowCreateConfirmation(false)}
                        maxWidth="sm"
                        fullWidth
                        PaperProps={{
                          sx: {
                            borderRadius: 2,
                            padding: 1,
                          },
                        }}
                        slotProps={{
                          backdrop: {
                            sx: {
                              backgroundColor: 'rgba(0, 0, 0, 0.8)',
                            },
                          },
                        }}
                      >
                        <DialogTitle
                          sx={{
                            fontSize: '1.5rem',
                            fontWeight: 600,
                            paddingTop: 3,
                            paddingBottom: 1,
                          }}
                        >
                          Create Embeddings
                        </DialogTitle>
                        <DialogContent sx={{ paddingTop: 2, paddingBottom: 3 }}>
                          <DialogContentText
                            sx={{
                              fontSize: '1rem',
                              color: 'text.secondary',
                              lineHeight: 1.6,
                            }}
                          >
                            Have you reviewed all the embeddings you are about to create?
                          </DialogContentText>
                        </DialogContent>
                        <DialogActions sx={{ padding: 2, paddingTop: 0, gap: 1 }}>
                          <Button
                            onClick={() => setShowCreateConfirmation(false)}
                            variant="outlined"
                            sx={{
                              minWidth: 100,
                              textTransform: 'none',
                              fontSize: '0.95rem',
                            }}
                          >
                            No
                          </Button>
                          <Button
                            onClick={() => {
                              setShowCreateConfirmation(false);
                              handleCreateEmbeddings();
                            }}
                            variant="contained"
                            color="success"
                            autoFocus
                            sx={{
                              minWidth: 100,
                              textTransform: 'none',
                              fontSize: '0.95rem',
                            }}
                          >
                            Yes
                          </Button>
                        </DialogActions>
                      </Dialog>
                    </>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-gray-600 mb-2">Session finished</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {isLoadingImages && <PageLoadingSpinner message="Loading camera image..." />}

        {/* Fallback when no images */}
        {!isLoadingImages && !preloadedImage && (
          <div className="bg-white rounded-lg shadow-md p-6 flex-1">
            <div className="text-center py-12">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">No Images Available</h2>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CandidateDetail;
